// src/components/admin/CommunityManager.tsx
// DROP 18a — Manage all 26 communities (22 villages + 4 special)

import React, { useState, useEffect, useRef } from "react";
import {
  Globe, Edit2, Save, X, Upload, Image,
  ChevronDown, ChevronUp, Users, MapPin, RefreshCw
} from "lucide-react";

interface Community {
  id: string;
  name: string;
  displayName: string;
  icon: string;
  type: "village" | "special";
  editable: boolean; // name editable for special communities
  description: string;
  population?: string;
  location?: string;
  leadership?: string;
  history?: string;
  coverImage?: string;
  galleryImages?: string[];
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  updatedAt?: string;
}

export default function CommunityManager() {
  const [communities, setCommunities] = useState<Community[]>([]);
  const [expanded,    setExpanded]    = useState<string | null>(null);
  const [editing,     setEditing]     = useState<string | null>(null);
  const [editData,    setEditData]    = useState<Partial<Community>>({});
  const [uploading,   setUploading]   = useState(false);
  const [msg,         setMsg]         = useState<{type:"ok"|"err"; text:string}|null>(null);
  const [filter,      setFilter]      = useState<"all"|"village"|"special">("all");
  const fileRef = useRef<HTMLInputElement>(null);

  const token = () => localStorage.getItem("erue_auth_token") || "";
  const flash = (type:"ok"|"err", text:string) => {
    setMsg({type,text}); setTimeout(() => setMsg(null),4000);
  };

  const load = async () => {
    try {
      const res = await fetch("/api/admin/communities", {
        headers: { Authorization: `Bearer ${token()}` }
      });
      const d = await res.json();
      if (d.success) setCommunities(d.communities || []);
    } catch { flash("err","Failed to load communities"); }
  };

  useEffect(() => { load(); }, []);

  const startEdit = (c: Community) => {
    setEditing(c.id);
    setEditData({ ...c });
    setExpanded(c.id);
  };

  const cancelEdit = () => { setEditing(null); setEditData({}); };

  const saveEdit = async (id: string) => {
    try {
      const res = await fetch(`/api/admin/communities/${id}`, {
        method: "PUT",
        headers: { "Content-Type":"application/json", Authorization:`Bearer ${token()}` },
        body: JSON.stringify(editData)
      });
      const d = await res.json();
      if (d.success) {
        flash("ok","Community updated successfully");
        setEditing(null); setEditData({}); await load();
      } else flash("err", d.error || "Failed to save");
    } catch { flash("err","Connection error"); }
  };

  const uploadImage = async (communityId: string, file: File) => {
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("folder", "communities");
      const res = await fetch("/api/community-portals/upload", {
        method: "POST",
        headers: { Authorization: `Bearer ${token()}` },
        body: form
      });
      const d = await res.json();
      if (d.success && d.url) {
        setEditData(prev => ({ ...prev, coverImage: d.url }));
        flash("ok", "Image uploaded — save to apply");
      } else flash("err", d.error || "Upload failed");
    } catch { flash("err","Upload error"); }
    finally { setUploading(false); }
  };

  const filtered = communities.filter(c =>
    filter === "all" ? true : c.type === filter
  );

  const inp = (label: string, field: keyof Community, placeholder = "") => (
    <div>
      <label className="text-xs uppercase tracking-wider mb-1 block"
             style={{ color: "rgba(237,232,220,0.4)" }}>{label}</label>
      <input value={(editData[field] as string) || ""}
             onChange={e => setEditData(prev => ({ ...prev, [field]: e.target.value }))}
             placeholder={placeholder}
             className="w-full px-3 py-2 rounded-lg text-sm outline-none"
             style={{ background: "#0E1812", border: "1px solid #1C2E22", color: "#EDE8DC" }}/>
    </div>
  );

  const txta = (label: string, field: keyof Community, rows = 3) => (
    <div>
      <label className="text-xs uppercase tracking-wider mb-1 block"
             style={{ color: "rgba(237,232,220,0.4)" }}>{label}</label>
      <textarea value={(editData[field] as string) || ""}
                onChange={e => setEditData(prev => ({ ...prev, [field]: e.target.value }))}
                rows={rows}
                className="w-full px-3 py-2 rounded-lg text-sm outline-none resize-none"
                style={{ background: "#0E1812", border: "1px solid #1C2E22", color: "#EDE8DC" }}/>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-bold text-lg" style={{ color: "#EDE8DC", fontFamily: "Cinzel, serif" }}>
            Community Management
          </h2>
          <p className="text-xs" style={{ color: "rgba(237,232,220,0.4)" }}>
            {communities.length} communities ({communities.filter(c => c.type==="village").length} villages + {communities.filter(c=>c.type==="special").length} special)
          </p>
        </div>
        <button onClick={load} className="p-2 rounded-lg border transition-all"
                style={{ borderColor: "#1C2E22", color: "rgba(237,232,220,0.5)" }}>
          <RefreshCw size={14}/>
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-4">
        {(["all","village","special"] as const).map(f => (
          <button key={f} onClick={() => setFilter(f)}
                  className="px-4 py-1.5 rounded-lg text-xs font-medium transition-all border"
                  style={{
                    background: filter===f ? "#C9A84C" : "transparent",
                    borderColor: filter===f ? "#C9A84C" : "#1C2E22",
                    color: filter===f ? "#070D0A" : "rgba(237,232,220,0.5)"
                  }}>
            {f === "all" ? `All (${communities.length})` :
             f === "village" ? `Villages (${communities.filter(c=>c.type==="village").length})` :
             `Special (${communities.filter(c=>c.type==="special").length})`}
          </button>
        ))}
      </div>

      {/* Flash */}
      {msg && (
        <div className="mb-4 px-4 py-3 rounded-xl text-sm flex items-center gap-2"
             style={{
               background: msg.type==="ok" ? "rgba(74,222,128,0.1)" : "rgba(239,68,68,0.1)",
               border: `1px solid ${msg.type==="ok" ? "rgba(74,222,128,0.3)" : "rgba(239,68,68,0.3)"}`,
               color: msg.type==="ok" ? "#4ADE80" : "#FCA5A5"
             }}>
          {msg.text}
        </div>
      )}

      {/* Community list */}
      <div className="space-y-2">
        {filtered.map(c => {
          const isOpen = expanded === c.id;
          const isEdit = editing === c.id;
          return (
            <div key={c.id} className="rounded-xl overflow-hidden border transition-all"
                 style={{ borderColor: isEdit ? "rgba(201,168,76,0.3)" : "#1C2E22", background: "#121E16" }}>
              {/* Row header */}
              <div className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3">
                  <span className="text-xl">{c.icon}</span>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm" style={{ color: "#EDE8DC" }}>
                        {c.displayName || c.name}
                      </span>
                      {c.type === "special" && (
                        <span className="text-xs px-2 py-0.5 rounded-full border"
                              style={{ borderColor: "rgba(201,168,76,0.3)", color: "#C9A84C" }}>
                          Special
                        </span>
                      )}
                    </div>
                    {c.updatedAt && (
                      <span className="text-xs" style={{ color: "rgba(237,232,220,0.3)" }}>
                        Updated {new Date(c.updatedAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => startEdit(c)}
                          className="p-1.5 rounded-lg transition-all"
                          style={{ color: "#C9A84C" }}>
                    <Edit2 size={14}/>
                  </button>
                  <button onClick={() => setExpanded(isOpen ? null : c.id)}
                          className="p-1.5 rounded-lg transition-all"
                          style={{ color: "rgba(237,232,220,0.4)" }}>
                    {isOpen ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
                  </button>
                </div>
              </div>

              {/* Expanded view */}
              {isOpen && (
                <div className="border-t px-4 pb-4 pt-4"
                     style={{ borderColor: "#1C2E22" }}>
                  {isEdit ? (
                    <div className="space-y-3">
                      {/* Special communities have editable name */}
                      {c.type === "special" && inp("Community Name (editable)", "displayName", "e.g. Igala Community")}
                      {inp("Icon (emoji)", "icon", "👑")}
                      {inp("Location / Origin", "location", "e.g. Lagos, Nigeria")}
                      {inp("Population (approx)", "population", "e.g. 500 members")}
                      {inp("LOC Representative / Leadership", "leadership")}
                      {txta("Description / About", "description", 3)}
                      {txta("History", "history", 4)}
                      {inp("Contact Name", "contactName")}
                      {inp("Contact Phone", "contactPhone")}
                      {inp("Contact Email", "contactEmail")}

                      {/* Cover image */}
                      <div>
                        <label className="text-xs uppercase tracking-wider mb-2 block"
                               style={{ color: "rgba(237,232,220,0.4)" }}>Cover Image</label>
                        {editData.coverImage && (
                          <img src={editData.coverImage} alt="Cover"
                               className="w-full h-32 object-cover rounded-lg mb-2"/>
                        )}
                        <input ref={fileRef} type="file" accept="image/*" className="hidden"
                               onChange={e => e.target.files?.[0] && uploadImage(c.id, e.target.files[0])}/>
                        <button onClick={() => fileRef.current?.click()} disabled={uploading}
                                className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs border transition-all"
                                style={{ borderColor: "#1C2E22", color: "rgba(237,232,220,0.5)" }}>
                          <Upload size={13}/>
                          {uploading ? "Uploading…" : "Upload Image"}
                        </button>
                      </div>

                      <div className="flex gap-2 mt-2">
                        <button onClick={cancelEdit}
                                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs border"
                                style={{ borderColor: "#1C2E22", color: "rgba(237,232,220,0.5)" }}>
                          <X size={12}/> Cancel
                        </button>
                        <button onClick={() => saveEdit(c.id)}
                                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold"
                                style={{ background: "#C9A84C", color: "#070D0A" }}>
                          <Save size={12}/> Save Changes
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      {c.description && (
                        <div className="col-span-2">
                          <p className="text-xs mb-1" style={{ color: "rgba(237,232,220,0.4)" }}>Description</p>
                          <p className="text-sm" style={{ color: "rgba(237,232,220,0.7)" }}>{c.description}</p>
                        </div>
                      )}
                      {c.location && (
                        <div>
                          <p className="text-xs mb-0.5" style={{ color: "rgba(237,232,220,0.4)" }}>Location</p>
                          <p className="text-sm" style={{ color: "#EDE8DC" }}>{c.location}</p>
                        </div>
                      )}
                      {c.leadership && (
                        <div>
                          <p className="text-xs mb-0.5" style={{ color: "rgba(237,232,220,0.4)" }}>Leadership</p>
                          <p className="text-sm" style={{ color: "#EDE8DC" }}>{c.leadership}</p>
                        </div>
                      )}
                      {!c.description && !c.location && !c.leadership && (
                        <div className="col-span-2 text-center py-4">
                          <p className="text-xs" style={{ color: "rgba(237,232,220,0.3)" }}>
                            No content yet — click ✏️ to add information
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
